import type { ComponentPropsWithoutRef, JSX } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import type { KeyboardKeys } from './KeyboardKeyTypes';

export type KeyboardShortcutProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * @description If set to true, the keyboard shortcut will stretch the full width of its container.
   */
  fullWidth?: boolean;
  /**
   * @description The keys displayed.
   */
  keyboardKeys: KeyboardKeys | KeyboardKeys[];
  /**
   * @description A visual label for the keyboard shortcut.
   */
  label?: string;
};

const StyledKeyboardShortcut = styled.div<{ $fullWidth: boolean }>`
  align-items: center;
  display: flex;
  gap: var(--wui-space-02);
  ${({ $fullWidth }) => $fullWidth && 'width: 100%; justify-content: space-between;'}
`;

const StyledKey = styled.kbd`
  align-items: center;
  background: var(--wui-color-bg-surface-secondary);
  border-bottom: 1px solid var(--wui-color-border-secondary);
  border-radius: 4px;
  color: var(--wui-color-text);
  display: flex;
  font-family: InterVariable, sans-serif;

  /* Adds serif to uppercase "I" to distinguish it from "l": https://rsms.me/inter/glyphs/?g=I.1 */
  font-feature-settings: 'cv08' on;
  font-size: 10px;

  /* Adds a slash to the zero to distinguish it from the letter "O": https://rsms.me/inter/glyphs/?g=zero.tf.slash */

  /* Adds tabular numbers to ensure the width of the key is consistent */
  font-variant-numeric: slashed-zero tabular-nums;
  height: 20px;
  justify-content: center;
  line-height: 20px;
  min-width: 20px;
  padding: 0 var(--wui-space-01);
`;

const Label = styled.span`
  color: var(--wui-color-text);
  font-size: 12px;
`;

const KeysContainer = styled.div`
  display: flex;
  gap: var(--wui-space-01);
`;

const keyToString = (key: KeyboardKeys): string => {
  switch (
    key // eslint-disable-line @typescript-eslint/switch-exhaustiveness-check
  ) {
    // Arrow Keys
    case 'ArrowDown':
      return '↓';
    case 'ArrowLeft':
      return '←';
    case 'ArrowRight':
      return '→';
    case 'ArrowUp':
      return '↑';
    // Modifier Keys
    case 'Alt':
      return 'Alt';
    case 'Cmd':
    case 'Meta':
      if (/Mac|iPhone|iPad/.test(window.navigator.userAgent)) {
        return '⌘';
      }
      if (window.navigator.userAgent.includes('Win')) {
        return '⊞';
      }
      return '^';
    case 'Ctrl':
      return 'Ctrl';
    case 'Option':
      return '⌥';
    case 'Shift':
      return '⇧';
    // Whitespace Keys
    case 'Enter':
      return '↵';
    case 'Space':
      return 'Space';
    case 'Tab':
      return 'Tab';
    case 'Backspace':
      return 'Del';
    case 'Esc':
      return 'Esc';
    default:
      return key.toUpperCase();
  }
};

export const KeyboardShortcut = ({
  label,
  keyboardKeys,
  fullWidth = false,
  ...otherProps
}: KeyboardShortcutProps): JSX.Element => (
  <StyledKeyboardShortcut
    $fullWidth={fullWidth}
    {...otherProps}
  >
    {isNotNil(label) && <Label>{label}</Label>}
    <KeysContainer>
      {(Array.isArray(keyboardKeys) ? keyboardKeys : [keyboardKeys]).map((keyboardKey, index) => (
        <StyledKey
          // in this instance keyboardKey might not be unique, so index is our only option
          // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          {keyToString(keyboardKey)}
        </StyledKey>
      ))}
    </KeysContainer>
  </StyledKeyboardShortcut>
);

KeyboardShortcut.displayName = 'KeyboardShortcut';
